/**
 * Vignobles › Fiche domaine (/vignobles/domaine/:id) — coordonnées, mini-carte, présentation,
 * indicateurs, références du domaine et historique. « Compléter avec l'IA », « Commander ».
 */
import { useState } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Sparkles, MapPin, Phone, Mail, Globe, Navigation, Archive, Trash2, ShoppingBag } from 'lucide-react';
import { toast } from 'sonner';
import { vignoblesApi } from '../../services/api';
import { ImagePicker } from '../../components/wine/ImagePicker';
import CommanderDrawer from '../achats/CommanderDrawer';
import { usePublicMode } from '../../context/PublicModeContext';
import { Spinner, Tag, Button, SegmentedControl } from '../../components/ui/kit';
import { WineColorDot, SpecificityTags, MaskedValue, Hearts } from '../../components/wine/wine';
import { InlineEditable } from '../../components/wine/InlineEditable';
import { VoiceDictateButton } from '../../components/wine/VoiceDictateButton';
import { RadarEditor, GardeEditor } from '../cuvee/FicheCuvee';
import { Lightbox } from '../../components/wine/Lightbox';
import { RegionField } from '../../components/wine/RegionField';
import { AppellationField, AppellationHint } from '../../components/wine/AppellationField';
import WineMap from '../../components/map/WineMap';

const REF_FILTERS = [{ value: 'all', label: 'Toutes' }, { value: 'stock', label: 'En stock' }];

/** Ligne de coordonnée avec icône (valeur éditable en enfant). */
function ContactRow({ icon: Icon, children }) {
  return <p className="flex items-center gap-2"><Icon className="w-3.5 h-3.5 text-n500 shrink-0" /> {children}</p>;
}

export default function FicheDomaine() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const fromCuvee = location.state?.fromCuvee;
  const [zoom, setZoom] = useState(null);
  const { isPublic } = usePublicMode();
  const qc = useQueryClient();
  const [filter, setFilter] = useState('all');
  const [enriching, setEnriching] = useState(false);
  const [commanderOpen, setCommanderOpen] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ['domain', id], queryFn: () => vignoblesApi.domain(id) });

  const completeWithAI = async () => {
    setEnriching(true);
    try {
      const e = await vignoblesApi.enrich(data.domain.site_web || data.domain.name);
      // Ne remplit que les champs vides existants pour ne pas écraser une saisie manuelle.
      const cur = data.domain;
      const pick = (key, val) => (val && !cur[key] ? { [key]: val } : {});
      await vignoblesApi.update(id, {
        presentation: e.presentation || cur.presentation, region: e.region || cur.region,
        appellations: e.appellations?.length ? e.appellations : cur.appellations,
        specificities: e.specificities?.length ? e.specificities : cur.specificities,
        ...pick('site_web', e.site_web), ...pick('gps', e.gps), ...pick('city', e.city),
        ...pick('dept', e.dept), ...pick('address', e.address), ...pick('postal_code', e.postal_code),
        ...pick('logo', e.logo), ...pick('photo', e.photo),
        ...(e.phone && !cur.contact?.phone ? { phone: e.phone } : {}),
        ...(e.email && !cur.contact?.email ? { email: e.email } : {}),
      });
      qc.invalidateQueries({ queryKey: ['domain', id] });
      toast.success('Fiche complétée par l\'IA');
    } catch { toast.error('Échec IA'); } finally { setEnriching(false); }
  };

  if (isLoading || !data) return <div className="p-6"><Spinner /></div>;
  const { domain: d, stats, cuvees, history } = data;
  const archive = async () => { await vignoblesApi.archiveDomain(id, !d.archived); qc.invalidateQueries({ queryKey: ['domain', id] }); qc.invalidateQueries({ queryKey: ['domains'] }); toast.success(d.archived ? 'Domaine désarchivé' : 'Domaine archivé'); };
  const remove = async () => {
    if (!window.confirm(`Supprimer le domaine « ${d.name} » ?`)) return;
    try { await vignoblesApi.deleteDomain(id); qc.invalidateQueries({ queryKey: ['domains'] }); toast.success('Domaine supprimé'); navigate('/vignobles/domaines'); }
    catch (e) { toast.error(e.response?.data?.detail || e.message || 'Suppression impossible'); }
  };
  const cuveeRows = cuvees.filter((c) => !c.deguste && (filter === 'all' || c.stock > 0));
  const degusteRows = cuvees.filter((c) => c.deguste);
  const patch = async (field, value) => { await vignoblesApi.update(id, { [field]: value }); qc.invalidateQueries({ queryKey: ['domain', id] }); };
  const itineraire = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${d.name}, ${d.city || ''}, France`)}`;
  const travelMin = d.distance_km ? Math.round(d.distance_km / 80 * 60) : null;

  return (
    <div className="p-4 md:p-6 pb-[150px] md:pb-[150px] max-w-[1100px] overflow-x-hidden">
      <div className="flex items-center justify-between mb-4">
        <p className="text-[13px] text-n600">
          {fromCuvee && (
            <>
              <Link to={`/cave/cuvee/${fromCuvee.id}`} className="vd-link" data-testid="back-to-cuvee">← {fromCuvee.name || 'Retour à la bouteille'}</Link>
              <span className="text-n400"> · </span>
            </>
          )}
          <Link to="/vignobles" className="vd-link">Vignobles</Link> / {d.name}
        </p>
        <div className="flex gap-2">
          <Button variant="secondary" className="h-8 text-[12px]" onClick={completeWithAI} disabled={enriching} data-testid="enrich-domain"><Sparkles className="w-3.5 h-3.5" /> {enriching ? '…' : "Compléter avec l'IA"}</Button>
          <Button variant="primary" className="h-8 text-[12px]" onClick={() => setCommanderOpen(true)} data-testid="add-to-order-btn"><ShoppingBag className="w-3.5 h-3.5" /> Ajouter à la commande</Button>
          <Button variant="secondary" className="h-8 text-[12px]" onClick={archive} data-testid="domain-archive"><Archive className="w-3.5 h-3.5" /> {d.archived ? 'Désarchiver' : 'Archiver'}</Button>
          <Button variant="secondary" className="h-8 text-[12px] text-red-700" onClick={remove} data-testid="domain-delete"><Trash2 className="w-3.5 h-3.5" /></Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[300px_1fr] gap-8">
        {/* Colonne gauche */}
        <div className="space-y-4">
          <ImagePicker label="Photo ou logo" kind="photo" aspect="aspect-[4/3]"
            entityId={d.id} fileId={d.photo || d.logo}
            api={{
              upload: (eid, k, file) => vignoblesApi.uploadImage(eid, k, file),
              search: (eid) => vignoblesApi.searchImages(eid),
              fromUrl: (eid, k, url) => vignoblesApi.imageFromUrl(eid, k, url),
              clear: (eid, k) => vignoblesApi.clearImage(eid, k),
            }}
            onUploaded={() => qc.invalidateQueries({ queryKey: ['domain', id] })} />
          <div className="vd-card overflow-hidden h-[180px]" data-testid="mini-map">
            <WineMap domains={[d]} center={d.map_override || d.gps} zoom={8} selectedId={d.id} />
          </div>
          <Link to="/vignobles" className="vd-link text-[13px] inline-block">Carte complète →</Link>
          <div className="text-[13px] text-n700 space-y-1.5" data-testid="domain-contact">
            <ContactRow icon={MapPin}><InlineEditable value={d.address} placeholder="Ajouter l'adresse" onSave={(v) => patch('address', v)} testId="domain-address" /></ContactRow>
            <div className="grid grid-cols-2 gap-2">
              <ContactRow icon={MapPin}><InlineEditable value={d.postal_code} placeholder="Code postal" onSave={(v) => patch('postal_code', v)} testId="domain-postal" /></ContactRow>
              <InlineEditable value={d.city} placeholder="Ville" onSave={(v) => patch('city', v)} testId="domain-city" />
            </div>
            <ContactRow icon={Phone}><InlineEditable value={d.contact?.phone} placeholder="Ajouter un téléphone" onSave={(v) => patch('phone', v)} testId="domain-phone" /></ContactRow>
            <ContactRow icon={Mail}><InlineEditable value={d.contact?.email} placeholder="Ajouter un e-mail" onSave={(v) => patch('email', v)} testId="domain-email" /></ContactRow>
            <ContactRow icon={Globe}><InlineEditable value={d.site_web} placeholder="Ajouter un site web" onSave={(v) => patch('site_web', v)} testId="domain-website" /></ContactRow>
            {d.site_web && <a href={d.site_web.startsWith('http') ? d.site_web : `https://${d.site_web}`} target="_blank" rel="noreferrer" className="vd-link inline-block">Ouvrir le site →</a>}
            {d.distance_km && <p className="flex items-center gap-2"><Navigation className="w-3.5 h-3.5 text-n500" /> {d.distance_km} km · ~{travelMin} min</p>}
            <a href={itineraire} target="_blank" rel="noreferrer" className="vd-link inline-block pt-1">Itinéraire →</a>
          </div>
        </div>

        {/* Colonne principale */}
        <div>
          <div className="kicker flex items-center gap-1">
            <span>{d.region || 'Région à renseigner'}</span>
            {(d.appellations || []).length > 0 && <span>&nbsp;· {(d.appellations || []).join(' · ')}</span>}
          </div>
          <h1 className="font-title text-4xl lg:text-5xl leading-none mt-1 mb-2">
            <InlineEditable value={d.name} onSave={(v) => patch('name', v)} testId="domain-name" displayClassName="cursor-text hover:text-gold700 transition-colors" />
          </h1>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-3 text-[13px]" data-testid="domain-appellation-principale">
            <span className="flex items-center gap-1">
              <span className="text-n500">Région</span>
              <RegionField value={d.region} testId="domain-region" onSave={(v) => patch('region', v)} inputClassName="h-7 text-[13px]" />
            </span>
            <span className="flex items-center gap-1">
              <span className="text-n500">Appellation principale</span>
              <AppellationField value={d.appellation_principale} testId="domain-appellation-principale-field"
                onSave={(v) => patch('appellation_principale', v)}
                onPick={({ vignoble }) => { if (vignoble && !d.region) patch('region', vignoble); }}
                inputClassName="h-7 text-[13px]" />
            </span>
          </div>
          <AppellationHint value={d.appellation_principale} />
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <SpecificityTags keys={d.specificities || []} max={5} />
            {d.direct_supplier && <Tag tone="accent">Fournisseur direct</Tag>}
            {d.hearts > 0 && <Hearts count={d.hearts} size={13} />}
          </div>
          <div className="hair-t hair-b py-3 mb-4" data-testid="domain-presentation">
            <p className="kicker mb-1 flex items-center gap-2">
              Présentation
              <VoiceDictateButton onTranscribe={(t) => patch('presentation', d.presentation ? `${d.presentation}\n${t}` : t)} testId="domain-presentation-mic" />
            </p>
            <div className="italic text-[14px] text-ink/90 leading-[1.5]">
              <InlineEditable type="textarea" value={d.presentation} placeholder="Ajouter une présentation…" onSave={(v) => patch('presentation', v)} testId="domain-presentation-edit" displayClassName="block min-h-[24px]" />
            </div>
          </div>

          <div className="vd-card flex flex-wrap divide-x divide-hair mb-6" data-testid="domain-stats">
            <div className="flex-1 min-w-[120px] px-4 py-3"><p className="kicker">En cave</p><p className="text-2xl nums">{stats.bottles} btl</p></div>
            <div className="flex-1 min-w-[120px] px-4 py-3"><p className="kicker">Sur la carte</p><p className="text-2xl nums">{stats.on_wine_list} vins</p></div>
            <div className="flex-1 min-w-[120px] px-4 py-3"><p className="kicker">Achats de l'année</p><p className="text-2xl nums"><MaskedValue masked={isPublic || !data.can_see_financials}>—</MaskedValue></p></div>
            <div className="flex-1 min-w-[120px] px-4 py-3"><p className="kicker">Dernière visite</p><p className="text-lg">{d.last_visit || '—'}</p></div>
          </div>

          <div className="vd-card p-4 mb-6" data-testid="domain-profile">
            <p className="kicker mb-3">Profil type des vins & garde</p>
            <div className="grid md:grid-cols-2 gap-6 items-center">
              <RadarEditor radar={d.radar} resetKey={d.id} compact onSave={(radar) => patch('radar', radar)} />
              <GardeEditor garde={d.garde} resetKey={d.id} compact onSave={(garde) => patch('garde', garde)} />
            </div>
            <div className="mt-3">
              <p className="kicker mb-1 flex items-center gap-2">
                Note
                <VoiceDictateButton onTranscribe={(t) => patch('deg_note', d.deg_note ? `${d.deg_note}\n${t}` : t)} testId="domain-deg-note-mic" />
              </p>
              <InlineEditable type="textarea" value={d.deg_note} testId="domain-deg-note"
                placeholder="Note libre sur le style du domaine…"
                displayClassName="block w-full min-h-[40px] text-[13px] text-n700" inputClassName="w-full text-[13px]"
                onSave={(v) => patch('deg_note', v)} />
            </div>
          </div>

          {(data.appellations || []).length > 0 && (
            <div className="mb-5" data-testid="domain-bottle-appellations">
              <p className="kicker mb-1.5">Appellations des bouteilles</p>
              <div className="flex flex-wrap gap-1.5">
                {data.appellations.map((a) => (
                  <Tag key={a} tone="neutral">{a}</Tag>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mb-2">
            <h3 className="font-title text-2xl">Nos références de ce domaine</h3>
            <SegmentedControl options={REF_FILTERS} value={filter} onChange={setFilter} testId="ref-filter" />
          </div>
          <div className="vd-card overflow-hidden">
            <table className="w-full text-[13.5px]" data-testid="domain-references">
              <thead><tr className="text-left kicker">
                <th className="py-2.5 px-3">Cuvée</th><th className="py-2.5 px-3">Millésimes</th>
                <th className="py-2.5 px-3">Formats</th><th className="py-2.5 px-3 text-right">Stock</th><th className="py-2.5 px-3">Statut</th>
              </tr></thead>
              <tbody>
                {cuveeRows.map((c) => (
                  <tr key={c.id} className="hair-t hover:bg-surface/60 cursor-pointer" onClick={() => navigate(`/cave/cuvee/${c.id}`)} data-testid={`ref-row-${c.id}`}>
                    <td className="py-2.5 px-3"><span className="flex items-center gap-2"><WineColorDot color={c.color} size={9} /> {c.name}</span></td>
                    <td className="py-2.5 px-3 nums text-n600">{c.years.join(' · ') || '—'}</td>
                    <td className="py-2.5 px-3 text-n600">{c.formats.join(', ') || '—'}</td>
                    <td className="py-2.5 px-3 text-right nums">{c.stock}</td>
                    <td className="py-2.5 px-3"><Tag tone={c.status === 'Sur la carte' ? 'fill' : 'neutral'}>{c.status}</Tag></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {degusteRows.length > 0 && (
            <div className="mt-6">
              <h3 className="font-title text-2xl mb-2">Références dégustées</h3>
              <div className="vd-card overflow-hidden">
                <table className="w-full text-[13.5px]" data-testid="domain-deguste-references">
                  <thead><tr className="text-left kicker">
                    <th className="py-2.5 px-3">Cuvée</th><th className="py-2.5 px-3">Dégustation</th>
                    <th className="py-2.5 px-3 text-right">Statut</th>
                  </tr></thead>
                  <tbody>
                    {degusteRows.map((c) => (
                      <tr key={c.id} className="hair-t hover:bg-surface/60 cursor-pointer" onClick={() => navigate(`/cave/degustes?open=${c.id}`)} data-testid={`deguste-ref-row-${c.id}`}>
                        <td className="py-2.5 px-3"><span className="flex items-center gap-2"><WineColorDot color={c.color} size={9} /> {c.name}</span></td>
                        <td className="py-2.5 px-3 text-n600">{c.years.join(' · ') || '—'}</td>
                        <td className="py-2.5 px-3 text-right"><Tag tone="accent">Dégusté</Tag></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {history?.length > 0 && (
            <div className="mt-6">
              <h3 className="font-title text-2xl mb-2">Historique</h3>
              {history.map((h, i) => <p key={i} className="text-[13px] text-n700 py-1 hair-b">{h.date} · {h.text}</p>)}
            </div>
          )}
        </div>
      </div>
      <CommanderDrawer open={commanderOpen} onClose={() => setCommanderOpen(false)} domain={d} cuvees={cuvees} />
      {/* Espace de défilement pour permettre de remonter « Nos références » en haut de l'écran */}
      <div className="h-[45vh]" aria-hidden data-testid="domain-scroll-spacer" />
      {zoom && <Lightbox src={zoom} alt={d.name} onClose={() => setZoom(null)} />}
    </div>
  );
}
